import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import styled from '@mui/material/styles/styled';
import { Point, UserEmblem } from '../../../../assets/images';
import ProgressBar from '../../../../components/charts/progressBar';
import { useLevels } from '../../../../context/levels';

// Styled components
const PaperCard = styled(Box)(({ theme, background }) => ({
    borderRadius: '20px',
    padding: '20px',
    boxShadow: theme.palette.shadows?.custom?.card || '0px 2px 8px rgba(0, 0, 0, 0.1)',
    border: 'none',
    position: 'relative',
    background: background || theme.palette.figma.block,
}));

const UserInfoCard = ({
    userData = {},
    sx = {},
    background = null,
    hidePoint = false
}) => {
    const theme = useTheme();
    const { userLevels } = useLevels();

    // userData에서 정보 추출 (exp 또는 experience, point 또는 points 지원)
    const userExp = userData?.exp || 0;
    const userPoint = userData?.point || 0;
    const userName = userData?.name || '사용자';

    // 경험치 기반 레벨 계산
    const calculateLevel = () => {
        if (!userLevels || userLevels.length === 0) return 1;

        // 경험치가 0이면 레벨 1
        if (userExp <= 0) return 1;

        // 레벨 99를 초과하면 99로 제한
        const maxLevel = userLevels[userLevels.length - 1]?.level || 99;

        // 경험치에 맞는 레벨 찾기
        for (let i = userLevels.length - 1; i >= 0; i--) {
            const levelData = userLevels[i];
            if (userExp >= levelData.totalExp) {
                return Math.min(levelData.level, maxLevel);
            }
        }

        return 1;
    };

    const userLevel = userData?.level || calculateLevel();

    // 현재 레벨의 requiredExp와 이전 레벨의 totalExp 계산
    const currentLevelData = userLevels?.find(level => level.level === userLevel);
    const prevLevelData = userLevels?.find(level => level.level === userLevel - 1);
    const requiredExp = userLevel > 99 ? 50000 : (currentLevelData?.requiredExp || 0);
    const currentExp = userLevel > 99 ? 50000 : (userExp - (prevLevelData?.totalExp || 0));

    return (
        <PaperCard
            sx={{
                marginBottom: 0,
                padding: '5px 10px 20px 10px',
                ...sx,
            }}
            background={background}
        >
            <Box
                sx={{
                    display: 'flex',
                    gap: 2.5,
                    alignItems: 'center',
                    alignSelf: 'stretch',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >

                    <Box sx={{
                        width: '120px',
                        height: '120px',
                        aspectRatio: '1/1',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                        <Box
                            sx={{
                                mb: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100px',
                                height: '100px',
                                padding: '5px',
                                flexShrink: '0',
                            }}
                        >
                            <UserEmblem level={userLevel} />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            position: 'relative',
                            width: 100,
                            height: 20,
                        }}
                    >
                        <ProgressBar
                            rangeStart={0}
                            rangeEnd={requiredExp}
                            start={0}
                            end={currentExp}
                            showValueLabel={true}
                            height="20px"
                            backgroundColor={theme.palette.figma.white[30]}
                            showCurrentIndicator={false}
                            progressColor={theme.palette.figma.steps}
                            sx={{ alignSelf: 'stretch' }}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        alignItems: 'flex-start',
                        gap: '5px',
                        flex: '1 0 0',
                        alignSelf: 'stretch',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            height: '54px',
                            alignItems: 'center',
                            gap: '5px',
                            alignSelf: 'stretch',
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '60px',
                                fontWeight: 700,
                                lineHeight: 1,
                                textAlign: 'left',
                                letterSpacing: '-1.8px',
                            }}
                        >
                            LV.{userLevel}
                        </Typography>
                    </Box>
                    <Typography
                        sx={{
                            fontSize: '18px',
                            color: theme.palette.figma.white[80],
                            textAlign: 'left',
                            fontWeight: 500,
                            letterSpacing: '-0.54px',
                        }}
                    >
                        {userName}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            height: '20px',
                        }}
                    > {!hidePoint && (
                        <>
                            <Point size={20} color="#FFCC00" />
                            <Typography
                                sx={{
                                    fontSize: '20px',
                                    fontWeight: 700,
                                    letterSpacing: '-0.6px',
                                }}
                            >
                                {userPoint.toLocaleString()}
                            </Typography>
                        </>
                    )}
                    </Box>

                </Box>
            </Box>
        </PaperCard>
    );
};

export default UserInfoCard;
